import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
} from "reactstrap";
import * as XLSX from "xlsx";
import { getReservationsByDateRange } from "../helpers/api.ts";
import Swal from "sweetalert2";
import moment from "moment";

const ReservationExcelExport = () => {
  const [modal, setModal] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const validateDateRange = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setError("Please select both start and end dates");
      return false;
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    if (startDate > endDate) {
      setError("Start date cannot be after end date");
      return false;
    }

    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 365) {
      setError("Date range cannot exceed 365 days");
      return false;
    }

    return true;
  };

  const formatReservationData = (reservations, summary) => {
    const safeParseNumber = (value) => {
      if (value === null || value === undefined || value === "") return 0;
      if (typeof value === "number") return isNaN(value) ? 0 : value;
      if (typeof value === "string") {
        const cleaned = value.trim();
        const numberMatch = cleaned.match(/^-?\d+(?:\.\d+)?/);
        if (numberMatch) {
          const parsed = parseFloat(numberMatch[0]);
          if (!isNaN(parsed)) return parsed;
        }
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    };

    // ── Summary sheet ────────────────────────────────────────────────────────
    const summaryData = [
      ["RESERVATION EXPORT SUMMARY"],
      ["Export Date", moment().format("DD-MM-YYYY HH:mm:ss")],
      [
        "Date Range",
        `${moment(dateRange.startDate).format("DD-MM-YYYY")} to ${moment(
          dateRange.endDate
        ).format("DD-MM-YYYY")}`,
      ],
      [""],
      ["SUMMARY"],
      ["Total Reservations", summary.totalReservations],
      ["Total Revenue", `₹${summary.totalRevenue}`],
      ["Total Discount", `₹${summary.totalDiscount}`],
      ["Net Revenue", `₹${summary.totalRevenue - summary.totalDiscount}`],
      [""],
      ["STATUS BREAKDOWN"],
      ["Pending", summary.statusBreakdown.PENDING],
      ["Confirmed", summary.statusBreakdown.CONFIRMED],
      ["Checked In", summary.statusBreakdown.CHECKED_IN],
      ["Checked Out", summary.statusBreakdown.CHECKED_OUT],
      ["Cancelled", summary.statusBreakdown.CANCELLED],
    ];

    // ── Reservations sheet — फक्त रुमची माहिती ──────────────────────────────
    const reservationsData = [
      [
        "Sr No",            // 1
        "Reservation ID",   // 2
        "Guest Name",       // 3
        "Mobile Number",    // 4
        "Email",            // 5
        "Room Number",      // 6
        "Room Type",        // 7
        "AC / Non-AC",      // 8
        "Check In Date",    // 9
        "Check In Time",    // 10
        "Check Out Date",   // 11
        "Check Out Time",   // 12
        "Number of Nights", // 13
        "Status",           // 14
        "Tariff Charges",   // 15
        "Advance Amount",   // 16
        "Amount Status",    // 17
        "Discount",         // 18
        "Payment Mode",     // 19
        "Guest Type",       // 20
        "Package",          // 21
        "Booking Source",   // 22
        "Extra Bed Charges",// 23
        "Remark",           // 24
        "Partners Count",   // 25
        "Hotel Name",       // 26
      ],
    ];

    reservations.forEach((reservation, idx) => {
      const {
        reservation_details,
        guest_details,
        room_details,
        hotel_details,
      } = reservation;

      reservationsData.push([
        idx + 1,
        reservation_details.reservation_id,
        guest_details.guest_name,
        guest_details.mobile_number,
        guest_details.email || "N/A",

        // ── Room info ────────────────────────────────────────────────────────
        room_details.room_number,
        room_details.room_type,
        room_details.ac_non_ac || "N/A",

        // ── Dates ────────────────────────────────────────────────────────────
        moment(reservation_details.check_in_date).format("DD-MM-YYYY"),
        reservation_details.check_in_time || "N/A",
        moment(reservation_details.check_out_date).format("DD-MM-YYYY"),
        reservation_details.check_out_time || "N/A",
        reservation_details.number_of_nights,

        // ── Status & charges ─────────────────────────────────────────────────
        reservation_details.status,
        `₹${safeParseNumber(reservation_details.tariff_charges)}`,
        `₹${safeParseNumber(reservation_details.Amount_Advance)}`,
        reservation_details.Amount_Status || "N/A",
        `₹${safeParseNumber(reservation_details.discount)}`,
        reservation_details.payment_mode || "N/A",

        // ── Other ────────────────────────────────────────────────────────────
        reservation_details.guest_type || "N/A",
        reservation_details.package || "N/A",
        reservation_details.booking_source || "N/A",
        `₹${safeParseNumber(reservation_details.extrabedcharges)}`,
        reservation_details.remark || "N/A",
        guest_details.partners?.length || 0,
        hotel_details.hotel_name,
      ]);
    });

    return { summaryData, reservationsData };
  };

  const exportToExcel = async () => {
    if (!validateDateRange()) return;

    setIsLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const hotel_id =
        user?.role === "HOTEL_ADMIN" ? user?.hotel_id : user?.hotel?.hotel_id;

      if (!hotel_id) {
        setError("Hotel ID not found. Please login again.");
        setIsLoading(false);
        return;
      }

      const response = await getReservationsByDateRange({
        hotel_id,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      });

      if (response.success) {
        const { reservations, summary } = response;
        const { summaryData, reservationsData } = formatReservationData(
          reservations,
          summary
        );

        const workbook = XLSX.utils.book_new();

        // Summary sheet
        const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
        summaryWS["!cols"] = [{ wch: 22 }, { wch: 32 }];
        XLSX.utils.book_append_sheet(workbook, summaryWS, "Summary");

        // Reservations sheet — column widths (रुमची माहिती)
        const reservationsWS = XLSX.utils.aoa_to_sheet(reservationsData);
        reservationsWS["!cols"] = [
          { wch: 6 },  // Sr No
          { wch: 14 }, // Reservation ID
          { wch: 22 }, // Guest Name
          { wch: 15 }, // Mobile Number
          { wch: 26 }, // Email
          { wch: 13 }, // Room Number
          { wch: 14 }, // Room Type
          { wch: 12 }, // AC/Non-AC
          { wch: 14 }, // Check In Date
          { wch: 13 }, // Check In Time
          { wch: 15 }, // Check Out Date
          { wch: 14 }, // Check Out Time
          { wch: 16 }, // Number of Nights
          { wch: 13 }, // Status
          { wch: 15 }, // Tariff Charges
          { wch: 15 }, // Advance Amount
          { wch: 15 }, // Amount Status
          { wch: 12 }, // Discount
          { wch: 14 }, // Payment Mode
          { wch: 13 }, // Guest Type
          { wch: 20 }, // Package
          { wch: 18 }, // Booking Source
          { wch: 17 }, // Extra Bed Charges
          { wch: 25 }, // Remark
          { wch: 14 }, // Partners Count
          { wch: 22 }, // Hotel Name
        ];
        XLSX.utils.book_append_sheet(workbook, reservationsWS, "Reservations");

        const startFmt = moment(dateRange.startDate).format("DD-MM-YYYY");
        const endFmt   = moment(dateRange.endDate).format("DD-MM-YYYY");
        const filename = `Reservations_${startFmt}_to_${endFmt}.xlsx`;

        XLSX.writeFile(workbook, filename);

        Swal.fire({
          icon: "success",
          title: "Export Successful",
          html: `
            <div class="text-start">
              <p><strong>File:</strong> ${filename}</p>
              <p><strong>Total Reservations:</strong> ${summary.totalReservations}</p>
              <p><strong>Total Revenue:</strong> ₹${summary.totalRevenue}</p>
              <p><strong>Net Revenue:</strong> ₹${summary.totalRevenue - summary.totalDiscount}</p>
              <p><strong>Date Range:</strong> ${startFmt} to ${endFmt}</p>
            </div>
          `,
        });

        setModal(false);
        setDateRange({ startDate: "", endDate: "" });
      } else {
        setError(response.message || "Failed to fetch reservations");
      }
    } catch (error) {
      console.error("Error exporting reservations:", error);
      setError(
        error.response?.data?.message ||
          "Failed to export reservations. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setDateRange({ startDate: "", endDate: "" });
    setError("");
  };

  const handleModalToggle = () => {
    setModal(!modal);
    if (!modal) resetForm();
  };

  return (
    <>
      <Button
        color="success"
        onClick={handleModalToggle}
        className="d-flex align-items-center"
      >
        <i className="fas fa-file-excel me-2"></i>
        Export to Excel
      </Button>

      <Modal isOpen={modal} toggle={handleModalToggle} size="md">
        <ModalHeader toggle={handleModalToggle}>
          <i className="fas fa-file-excel me-2"></i>
          Export Reservations to Excel
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={12}>
                <Alert color="info" className="mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Select a date range to export reservations.
                  Export मध्ये फक्त <strong>रुमची माहिती</strong> (Room, Check-in/out dates, Tariff) येईल.
                </Alert>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="startDate">Start Date *</Label>
                  <Input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={dateRange.startDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="endDate">End Date *</Label>
                  <Input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={dateRange.endDate}
                    onChange={handleInputChange}
                    min={dateRange.startDate}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            {error && (
              <Row>
                <Col md={12}>
                  <Alert color="danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={12}>
                <div className="text-end mt-3">
                  <Button
                    color="secondary"
                    className="me-2"
                    onClick={handleModalToggle}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="success"
                    onClick={exportToExcel}
                    disabled={
                      isLoading || !dateRange.startDate || !dateRange.endDate
                    }
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-1"></i>
                        Export Excel
                      </>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ReservationExcelExport;